'use client';

import React, { RefObject, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

interface FilterDropdownPortalProps {
  isOpen: boolean;
  dropdownRef: RefObject<HTMLDivElement>;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  dropdownStyle: React.CSSProperties;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  children: ReactNode;
  selectedCount?: number;
  onClearAll?: () => void;
}

const FilterDropdownPortal: React.FC<FilterDropdownPortalProps> = ({
  isOpen,
  dropdownRef,
  scrollContainerRef,
  dropdownStyle,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  children,
  selectedCount = 0,
  onClearAll,
}): React.ReactElement | null => {
  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="bg-white rounded-xl shadow-lg border border-neutral-200 max-h-80 overflow-hidden flex flex-col max-w-xs"
      style={dropdownStyle}
      data-dropdown-portal
      data-testid="filter-dropdown-portal"
      role="listbox"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-200">
        <Icon icon={faSearch} className="text-neutral-400 text-sm" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          autoFocus
          className="flex-1 text-sm bg-transparent outline-none placeholder-neutral-400 text-neutral-900"
          data-testid="filter-search-input"
          aria-label="Search"
        />
      </div>
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto py-2">
        {children}
      </div>
      {selectedCount > 0 && onClearAll && (
        <div className="border-t border-neutral-200 px-3 py-2.5">
          <button
            type="button"
            onClick={onClearAll}
            className="text-sm text-tertiary hover:text-primary cursor-pointer"
            data-testid="filter-clear-all"
          >
            Clear {selectedCount} {selectedCount === 1 ? 'filter' : 'filters'}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FilterDropdownPortal;
